import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon, CloseIcon, SearchIcon } from "./Icons";
import { useLocale } from "../lib/LocaleProvider";
import { gregorianYear, typeLabel } from "../lib/legalTaxonomy";
import { localePath } from "../lib/localeRouting";

const DOCUMENT_TYPES = [
  "RULING",
  "DTA",
  "DTA_PROTOCOL",
  "RC",
  "ACT",
  "ED",
  "RDN",
  "RD",
  "MR",
  "MFN",
  "MFO",
  "DGN",
  "TP",
  "P",
  "RDC",
  "BTR",
  "JUDGMENT",
];

export default function SearchHeader({
  query,
  setQuery,
  language,
  setLanguage,
  documentType,
  setDocumentType,
  year,
  setYear,
  onSubmit,
  busy,
  comparisonCount,
  onOpenComparison,
  navigationMode,
  onNavigationModeChange,
  availableYears,
  searchRequest = 0,
}) {
  const { locale, en, setLocale, text, count } = useLocale();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const searchToggleRef = useRef(null);
  const searchInputRef = useRef(null);
  const isMobile = () => window.matchMedia("(max-width: 800px)").matches;

  useEffect(() => {
    if (mobileSearchOpen && isMobile()) searchInputRef.current?.focus();
  }, [mobileSearchOpen]);

  useEffect(() => {
    if (!searchRequest) return;
    if (isMobile()) setMobileSearchOpen(true);
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [searchRequest]);

  const openSearch = () => {
    onNavigationModeChange("search");
    if (isMobile()) {
      setMobileSearchOpen(true);
      window.requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  };

  const closeSearch = () => {
    setMobileSearchOpen(false);
    searchToggleRef.current?.focus();
  };

  const handleSearchSubmit = async (event) => {
    const succeeded = await onSubmit(event);
    if (succeeded === true && isMobile()) closeSearch();
  };

  const activeFilters = [
    language === "th" ? text("header.selectedLanguageThai") : null,
    language === "en" ? text("header.selectedLanguageEnglish") : null,
    documentType ? typeLabel(documentType, locale) : null,
    year ? (locale === "en" ? gregorianYear(null, year) : `พ.ศ. ${year}`) : null,
  ].filter(Boolean);
  const filterSummary = activeFilters.length
    ? text("header.activeFilters", { selection: activeFilters.join(" · ") })
    : text("header.noActiveFilters");
  const filterDisclosureLabel = `${text(mobileFiltersOpen ? "header.closeFilters" : "header.openFilters")}${activeFilters.length ? `, ${filterSummary}` : ""}`;

  return (
    <>
      <header className="app-header">
        <a className="brand" href={localePath(locale, "/")} aria-label={text("header.searchLabel")}>
          <img src="/assets/invictus-logo.png" alt="INVICTUS" />
          <span className="brand-divider" aria-hidden="true">/</span>
          <span className="brand-product">TAX LAW</span>
        </a>
        <button
          ref={searchToggleRef}
          type="button"
          className="mobile-search-toggle"
          aria-label={activeFilters.length ? `${text("header.searchLabel")}, ${filterSummary}` : text("header.searchLabel")}
          title={activeFilters.length ? filterSummary : undefined}
          aria-controls="search"
          aria-expanded={mobileSearchOpen}
          onClick={() => mobileSearchOpen ? closeSearch() : openSearch()}
        >
          <SearchIcon />
          {activeFilters.length > 0 && <span className="mobile-search-filter-count" aria-hidden="true">{activeFilters.length}</span>}
        </button>
        <nav className="header-nav" aria-label={text("header.menu")}>
          <button
            type="button"
            className={navigationMode === "search" ? "active" : ""}
            aria-current={navigationMode === "search" ? "page" : undefined}
            onClick={openSearch}
          >
            {text("header.search")}
          </button>
          <button
            type="button"
            className={navigationMode === "browse" ? "active" : ""}
            aria-current={navigationMode === "browse" ? "page" : undefined}
            onClick={() => {
              setMobileSearchOpen(false);
              onNavigationModeChange("browse");
            }}
          >
            {text("header.library")}
          </button>
          <button type="button" className="nav-compare" onClick={onOpenComparison}>
            {text("header.compare")}
            <span aria-label={text("comparison.count", { count: comparisonCount })}>{count(comparisonCount)}</span>
          </button>
        </nav>
        <div className="ui-language" aria-label={text("header.uiLanguage")}>
          <button
            type="button"
            className={!en ? "active" : ""}
            aria-pressed={!en}
            onClick={() => setLocale("th")}
          >
            TH
          </button>
          <button
            type="button"
            className={en ? "active" : ""}
            aria-pressed={en}
            onClick={() => setLocale("en")}
          >
            EN
          </button>
        </div>
      </header>
      <section
        className="search-band"
        id="search"
        data-mobile-open={mobileSearchOpen}
        onKeyDown={(event) => {
          if (event.key === "Escape" && isMobile()) {
            event.preventDefault();
            closeSearch();
          }
        }}
      >
        <div className="mobile-search-actions">
          <button type="button" className="search-panel-close" aria-label={text("common.close")} onClick={closeSearch}>
            <CloseIcon />
          </button>
        </div>
        <form className="search-form" onSubmit={handleSearchSubmit}>
          <label className="search-input">
            <span className="sr-only">{text("header.searchLabel")}</span>
            <SearchIcon />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={text("header.placeholder")}
              autoComplete="off"
            />
          </label>
          <button className="primary-button" type="submit" disabled={busy || !query.trim()}>
            <SearchIcon />
            <span>{busy ? text("header.searching") : text("header.submit")}</span>
          </button>
        </form>
        <button
          type="button"
          className="mobile-filter-toggle"
          aria-controls="search-filters"
          aria-expanded={mobileFiltersOpen}
          aria-label={filterDisclosureLabel}
          title={filterSummary}
          onClick={() => setMobileFiltersOpen((open) => !open)}
        >
          <span>{filterSummary}</span>
          <ChevronDownIcon />
        </button>
        <div className="filters" id="search-filters" data-mobile-open={mobileFiltersOpen} aria-label={text("header.filters")}>
          <label>
            <span>{text("header.language")}</span>
            <select
              value={language}
              aria-label={text("header.language")}
              onChange={(event) => setLanguage(event.target.value)}
            >
              <option value="">{text("header.all")}</option>
              <option value="th">{text("reader.thai")}</option>
              <option value="en">{text("header.english")}</option>
            </select>
          </label>
          <label>
            <span>{text("header.type")}</span>
            <select
              value={documentType}
              aria-label={text("header.type")}
              onChange={(event) => setDocumentType(event.target.value)}
            >
              <option value="">{text("header.all")}</option>
              {DOCUMENT_TYPES.map((code) => (
                <option value={code} key={code}>{typeLabel(code, locale)}</option>
              ))}
            </select>
          </label>
          <label className="date-filter">
            <span>{text("header.year")}</span>
            <select
              aria-label={text("header.year")}
              value={year}
              onChange={(event) => setYear(event.target.value)}
            >
              <option value="">{text("header.allYears")}</option>
              {availableYears.map((availableYear) => (
                <option key={availableYear} value={availableYear}>
                  {locale === "en" ? gregorianYear(null, availableYear) : `พ.ศ. ${availableYear}`}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>
    </>
  );
}
